import moment from "moment-timezone";
import { createSelector } from "reselect";
import {
  AccessLevelType,
  CONTINUOUS_DETECTION_TIME_DEFAULT,
  EMPTY_USER,
  MobileDeviceUserData,
  RoleType,
  StatusType,
  TagTypes,
  UserDevice,
  UserMobileDevice,
  UsersData,
  UsersFilterMap,
  UsersProfile,
  UsersSort,
  UsersSortType,
} from ".";
import { Device, DeviceData, DeviceType } from "../../redux/devices";
import {
  devicesSlicedBySelectedNode,
  organizationHasUserTag,
} from "../../redux/devices/selectors";
import { ActivityStatus, DevicesStatusMap } from "../../redux/devicesStatuses";

import { NodeType, OrganizationNode } from "../organization";
import {
  currentUserBaseNode,
  nodeById,
  organizationNodes,
  pathToNode,
  rootNode,
  userSelectedENDNodeId,
  userSelectedNodeId,
} from "../organization/selectors";
import { IAppState, SortDirection } from "../types";
import { authReady } from "../userProfile/selectors";

export const usersSortedByName = createSelector(
  (users: UsersProfile[]) => users,
  (_: UsersProfile[], order: string) => order,
  (users: UsersProfile[], order: string) => {
    if (order === undefined) return users;
    const compare = (a: UsersProfile, b: UsersProfile): number => {
      if (a?.firstName < b?.firstName) {
        return order === "ASC" ? -1 : 1;
      }
      if (a?.firstName > b?.firstName) {
        return order === "ASC" ? 1 : -1;
      }
      return 0;
    };
    return users.sort(compare);
  }
);

export const usersEmails = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (users) => {
    return Object.values(users).map((el) => (el?.email ? el.email : ""));
  }
);

export const usersSlicedBySelectedNode = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  rootNode,
  (
    users,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    rootNode
  ) => {
    const isUserUnderNode = (userNodeId: number, maybeTop: number) => {
      if (userSelectedNodeId === rootNode) return true;
      const selectedNodePath = pathToNode(organizationNodes, userNodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(users).filter((user: UsersProfile) => {
      return (
        isUserUnderNode(user.baseNodeId, userSelectedNodeId) &&
        user.roleName !== RoleType.API_USER
      );
    });
  }
);

export const usersSlicedBySelectedReportNode = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedENDNodeId,
  currentUserBaseNode,
  (users, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode) return Object.values(users);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(users).filter((resident: UsersProfile) =>
      isResidentUnderNode(resident.baseNodeId, userSelectedNodeId)
    );
  }
);

export const usersToDisplay = createSelector(
  usersSlicedBySelectedNode,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  (state: IAppState): UsersFilterMap => state.users.settings.filters,
  (state: IAppState): UsersSort => state.users.settings.sort,
  devicesSlicedBySelectedNode,
  (state: IAppState) => state.devicesStatuses.activityStatuses,
  (state: IAppState): MobileDeviceUserData => state.users.mobileDeviceUser,
  (_: IAppState, wordForRoot: string): string => wordForRoot,
  (
    users,
    organizationNodes,
    filters,
    sort,
    devices: Device[],
    activityStatuses,
    mobileDeviceUser,
    wordForRoot
  ) => {
    if (users.length === 0) return [];
    const rootOrganization = organizationNodes[0];

    const sortByName = (a: UsersProfile, b: UsersProfile): number => {
      if (a.firstName !== null && b.firstName === null)
        return sort.direction === "ASC" ? -1 : 1;
      if (a.firstName === null && b.firstName !== null)
        return sort.direction === "ASC" ? 1 : -1;
      if (!a.firstName && !b.firstName) return sortByEmail(a, b);
      if (a?.firstName?.toLowerCase() < b?.firstName?.toLowerCase()) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (a?.firstName?.toLowerCase() > b?.firstName?.toLowerCase()) {
        return sort.direction === "ASC" ? 1 : -1;
      }
    };
    const sortByCreation = (a: UsersProfile, b: UsersProfile): number => {
      if (
        a.activityStatusUpdatedOn !== null &&
        b.activityStatusUpdatedOn === null
      )
        return sort.direction === "ASC" ? -1 : 1;
      if (
        a.activityStatusUpdatedOn === null &&
        b.activityStatusUpdatedOn !== null
      )
        return sort.direction === "ASC" ? 1 : -1;
      if (
        a.activityStatusUpdatedOn === null &&
        b.activityStatusUpdatedOn === null
      )
        return sortByName(a, b);
      const aM = moment.utc(a.activityStatusUpdatedOn).local();
      const bM = moment.utc(b.activityStatusUpdatedOn).local();
      if (aM > bM) {
        return sort.direction === SortDirection.ASC ? -1 : 1;
      }
      if (aM < bM) {
        return sort.direction === SortDirection.ASC ? 1 : -1;
      }
      return sortByName(a, b);
    };

    const sortByEmail = (a: UsersProfile, b: UsersProfile): number => {
      if (a?.email?.toLowerCase() < b?.email?.toLowerCase()) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (a?.email?.toLowerCase() > b?.email?.toLowerCase()) {
        return sort.direction === "ASC" ? 1 : -1;
      }
      return 0;
    };
    const sortByBlock = (a: UsersProfile, b: UsersProfile): number => {
      const aBlock = nodeById(organizationNodes, a.baseNodeId);
      const bBlock = nodeById(organizationNodes, b.baseNodeId);
      const aTitle =
        aBlock?.type === NodeType.Root ? wordForRoot : aBlock?.title;
      const bTitle =
        bBlock?.type === NodeType.Root ? wordForRoot : bBlock?.title;

      if (aTitle?.toLowerCase() < bTitle?.toLowerCase()) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (aTitle?.toLowerCase() > bTitle?.toLowerCase()) {
        return sort.direction === "ASC" ? 1 : -1;
      }
      return sortByName(a, b);
    };
    const sortCondition = (a: UsersProfile, b: UsersProfile): number => {
      // if (a.status === StatusType.ACTIVE && b.status !== StatusType.ACTIVE) return -1;
      // if (a.status !== StatusType.ACTIVE && b.status === StatusType.ACTIVE) return 1;
      switch (sort.type) {
        case UsersSortType.LastActivity:
          return sortByCreation(a, b);
        case UsersSortType.Name:
          return sortByName(a, b);
        case UsersSortType.Email:
          return sortByEmail(a, b);
        case UsersSortType.Block:
          return sortByBlock(a, b);
      }

      return 0;
    };
    return users
      .filter((el) => {
        return filterCondition(
          el,
          filters,
          rootOrganization,
          devices,
          activityStatuses,
          configForMobileDEvice(mobileDeviceUser, el?.id, activityStatuses)
        );
      })

      .sort(sortCondition);
  }
);

export const getUsersByFilterTypesCreator = () => {
  return createSelector(
    usersSlicedBySelectedNode,
    organizationNodes,
    devicesSlicedBySelectedNode,
    (state: IAppState) => state.devicesStatuses.activityStatuses,
    organizationHasUserTag,
    (state: IAppState): MobileDeviceUserData => state.users.mobileDeviceUser,
    (
      users,
      organizationNodes,
      devices: Device[],
      activityStatuses,
      organizationHasUserTag,
      mobileDeviceUser
    ) => {
      if (users.length === 0) return {};
      const rootOrganization = organizationNodes[0];

      const byFilters = {};
      Object.values(StatusType).map((el) => (byFilters[el] = 0));
      Object.values(RoleType).map((el) => (byFilters[el] = 0));
      Object.values(AccessLevelType).map((el) => (byFilters[el] = 0));

      if (organizationHasUserTag) {
        Object.values(UserDevice).map((el) => (byFilters[el] = 0));
        Object.values(UserMobileDevice).map((el) => (byFilters[el] = 0));
      }
      users.map((el: UsersProfile) => {
        if (el.roleName === RoleType.ADMIN) {
          byFilters[RoleType.ADMIN] = byFilters[RoleType.ADMIN]
            ? byFilters[RoleType.ADMIN] + 1
            : 1;
        } else if (el.roleName === RoleType.VIEWER) {
          byFilters[RoleType.VIEWER] = byFilters[RoleType.VIEWER]
            ? byFilters[RoleType.VIEWER] + 1
            : 1;
        } else if (el.roleName === RoleType.DISPLAY) {
          byFilters[RoleType.DISPLAY] = byFilters[RoleType.DISPLAY]
            ? byFilters[RoleType.DISPLAY] + 1
            : 1;
        } else if (el.roleName === RoleType.CAREGIVER) {
          byFilters[RoleType.CAREGIVER] = byFilters[RoleType.CAREGIVER]
            ? byFilters[RoleType.CAREGIVER] + 1
            : 1;
        }

        if (el.status === StatusType.ACTIVE) {
          byFilters[StatusType.ACTIVE] = byFilters[StatusType.ACTIVE]
            ? byFilters[StatusType.ACTIVE] + 1
            : 1;
        } else if (el.status === StatusType.PENDING) {
          byFilters[StatusType.PENDING] = byFilters[StatusType.PENDING]
            ? byFilters[StatusType.PENDING] + 1
            : 1;
        } else if (el.status === StatusType.SUSPENDED) {
          byFilters[StatusType.SUSPENDED] = byFilters[StatusType.SUSPENDED]
            ? byFilters[StatusType.SUSPENDED] + 1
            : 1;
        }
        if (el.baseNodeId === rootOrganization.id)
          byFilters[AccessLevelType.ORGANIZATION] = byFilters[
            AccessLevelType.ORGANIZATION
          ]
            ? byFilters[AccessLevelType.ORGANIZATION] + 1
            : 1;
        else
          byFilters[AccessLevelType.BLOCK] = byFilters[AccessLevelType.BLOCK]
            ? byFilters[AccessLevelType.BLOCK] + 1
            : 1;
        if (devices.some((dev) => dev?.userId === el.id)) {
          const relatedDevice = devices.find((dev) => dev?.userId === el.id);
          const activityStatus = activityStatuses[relatedDevice.id];
          if (activityStatus && (activityStatus as ActivityStatus)?.isActive)
            byFilters[UserDevice.ONLINE] = byFilters[UserDevice.ONLINE]
              ? byFilters[UserDevice.ONLINE] + 1
              : 1;
          else
            byFilters[UserDevice.OFFLINE] = byFilters[UserDevice.OFFLINE]
              ? byFilters[UserDevice.OFFLINE] + 1
              : 1;
        } else
          byFilters[UserDevice.NO_DEVICE] = byFilters[UserDevice.NO_DEVICE]
            ? byFilters[UserDevice.NO_DEVICE] + 1
            : 1;
        const mobileDevice = configForMobileDEvice(
          mobileDeviceUser,
          el.id,
          activityStatuses
        );
        if (mobileDevice?.hasDevice) {
          if (mobileDevice?.online)
            byFilters[UserMobileDevice.ONLINE] = byFilters[
              UserMobileDevice.ONLINE
            ]
              ? byFilters[UserMobileDevice.ONLINE] + 1
              : 1;
          else
            byFilters[UserMobileDevice.OFFLINE] = byFilters[
              UserMobileDevice.OFFLINE
            ]
              ? byFilters[UserMobileDevice.OFFLINE] + 1
              : 1;
        } else
          byFilters[UserMobileDevice.NO_DEVICE] = byFilters[
            UserMobileDevice.NO_DEVICE
          ]
            ? byFilters[UserMobileDevice.NO_DEVICE] + 1
            : 1;
        return null;
      });
      return byFilters;
    }
  );
};

export const doesNodeContainsUsers = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (_: IAppState, nodeId: number): number => nodeId,
  (users, nodeId) => {
    return (
      Object.values(users).filter(
        (user: UsersProfile) => user.baseNodeId === nodeId
      ).length > 0
    );
  }
);

export const userSelected = createSelector(
  (state: IAppState): string[] => state.users.settings.selectedUsers,
  (_: IAppState, userId: string): string => userId,
  (selectded, userId) => {
    return selectded.indexOf(userId) !== -1;
  }
);

export const userSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] => state.users.settings.selectedUsers,
    (_: IAppState, userId: string): string => userId,
    (selectded, userId) => {
      return selectded.indexOf(userId) !== -1;
    }
  );
};

export const usersOnNodeCount = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  (_: IAppState, nodeId: number) => nodeId,
  (users, organizationNodes, nodeId) => {
    const isUserUnderNode = (userNodeId: number, maybeTop: number) => {
      // if (userNodeId === currentUserBaseNode) //myself
      //     return true;
      const selectedNodePath = pathToNode(organizationNodes, userNodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(users).filter((user: UsersProfile) =>
      isUserUnderNode(user.baseNodeId, nodeId)
    ).length;
  }
);

export const usersReady = createSelector(
  authReady,
  (state: IAppState): boolean => state.users.isLoading,
  (mainLoaded, isLoading) => {
    return !mainLoaded ? false : isLoading === false;
  }
);

export const usersFiltered = createSelector(
  usersSlicedBySelectedNode,
  organizationNodes,
  devicesSlicedBySelectedNode,
  (state: IAppState) => state.devicesStatuses.activityStatuses,
  (state: IAppState): MobileDeviceUserData => state.users.mobileDeviceUser,
  (_: IAppState, filters: UsersFilterMap) => filters,
  (
    users: UsersProfile[],
    organizationNodes,
    devices: Device[],
    activityStatuses,
    mobileDeviceUser,
    filters: UsersFilterMap
  ) => {
    const rootOrganization = organizationNodes[0];
    return users.filter((el) => {
      return filterCondition(
        el,
        filters,
        rootOrganization,
        devices,
        activityStatuses,
        configForMobileDEvice(mobileDeviceUser, el?.id, activityStatuses)
      );
    });
  }
);

export const isSelectionMode = createSelector(
  (state: IAppState): string[] => state.users.settings.selectedUsers,
  (selectedUsers) => {
    return selectedUsers.length > 0;
  }
);

export const toWhomOwnershipCanBeTransfered = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  (users, nodes) => {
    const rootNodeId = nodes[0]?.id;
    if (!rootNodeId) return [];
    return Object.values(users).filter(
      (el) =>
        el?.baseNodeId === rootNodeId &&
        el.roleName === RoleType.ADMIN &&
        el.status === StatusType.ACTIVE
    );
  }
);

export const userById = createSelector(
  (state: IAppState): UsersData => state.users.users,
  (_: IAppState, id: string): string => id,
  (users, id) => {
    return Object.values(users).find((el) => el.id === id);
  }
);

export const userByIdCreator = () => {
  return createSelector(
    (state: IAppState): UsersData => state.users.users,
    (_: IAppState, id: string): string => id,
    (users, id) => {
      // console.log(id, Object.keys(users).length, users[id]);
      if (Object.keys(users).length === 0 || users[id] === undefined)
        return EMPTY_USER;
      console.log(users[id]);
      return users[id];
    }
  );
};

const filterCondition = (
  el: UsersProfile,
  filters: UsersFilterMap,
  rootOrganization: OrganizationNode,
  devices: Device[],
  activityStatuses: DevicesStatusMap,
  userConfig: {
    enabled: boolean;
    hasDevice: boolean;
    online: boolean;
    configName: string;
  },
  logic = "AND"
) => {
  let meetAnycondition = false;
  let meetAndCondition = undefined;
  if (
    Object.values(filters).every((value) => {
      return !value;
    })
  )
    return true;
  if (filters[RoleType.ADMIN])
    meetAnycondition = meetAnycondition || el.roleName === RoleType.ADMIN;
  if (filters[RoleType.VIEWER])
    meetAnycondition = meetAnycondition || el.roleName === RoleType.VIEWER;
  if (filters[RoleType.CAREGIVER])
    meetAnycondition = meetAnycondition || el.roleName === RoleType.CAREGIVER;
  if (filters[RoleType.DISPLAY])
    meetAnycondition = meetAnycondition || el.roleName === RoleType.DISPLAY;
  if (
    filters[RoleType.ADMIN] ||
    filters[RoleType.VIEWER] ||
    filters[RoleType.CAREGIVER] ||
    filters[RoleType.DISPLAY]
  )
    meetAndCondition =
      (meetAndCondition === undefined ? true : meetAndCondition) &&
      (filters[RoleType.ADMIN] &&
      filters[RoleType.VIEWER] &&
      filters[RoleType.CAREGIVER] &&
      filters[RoleType.DISPLAY]
        ? true
        : Object.keys(filters)
            .filter(
              (key) =>
                filters[key] === true &&
                Object.values(RoleType).indexOf(key as RoleType) !== -1
            )
            .indexOf(el.roleName) !== -1);

  if (filters[StatusType.ACTIVE])
    meetAnycondition = meetAnycondition || el.status === StatusType.ACTIVE;
  if (filters[StatusType.PENDING])
    meetAnycondition = meetAnycondition || el.status === StatusType.PENDING;
  if (filters[StatusType.SUSPENDED])
    meetAnycondition = meetAnycondition || el.status === StatusType.SUSPENDED;
  if (
    filters[StatusType.ACTIVE] ||
    filters[StatusType.PENDING] ||
    filters[StatusType.SUSPENDED]
  )
    meetAndCondition =
      (meetAndCondition === undefined ? true : meetAndCondition) &&
      (filters[StatusType.ACTIVE] &&
      filters[StatusType.PENDING] &&
      filters[StatusType.SUSPENDED]
        ? true
        : Object.keys(filters)
            .filter(
              (key) =>
                filters[key] === true &&
                Object.values(StatusType).indexOf(key as StatusType) !== -1
            )
            .indexOf(el.status) !== -1);

  // [StatusType.ACTIVE]
  //     ? el.status === StatusType.ACTIVE
  //     : filters[StatusType.PENDING]
  //     ? el.status === StatusType.PENDING
  //     : el.status === StatusType.SUSPENDED);

  if (filters[AccessLevelType.ORGANIZATION])
    meetAnycondition =
      meetAnycondition || el.baseNodeId === rootOrganization.id;
  if (filters[AccessLevelType.BLOCK])
    meetAnycondition =
      meetAnycondition || el.baseNodeId !== rootOrganization.id;

  if (filters[AccessLevelType.ORGANIZATION] || filters[AccessLevelType.BLOCK])
    meetAndCondition =
      (meetAndCondition === undefined ? true : meetAndCondition) &&
      (filters[AccessLevelType.ORGANIZATION] && filters[AccessLevelType.BLOCK]
        ? true
        : filters[AccessLevelType.ORGANIZATION]
        ? el.baseNodeId === rootOrganization.id
        : el.baseNodeId !== rootOrganization.id);
  const device = devices.find((dev) => dev?.userId === el.id);

  if (device) {
    const active =
      device === undefined
        ? undefined
        : (activityStatuses[device?.id] as ActivityStatus)?.isActive || false;
    if (filters[UserDevice.OFFLINE])
      meetAnycondition = meetAnycondition || active === false;
    if (filters[UserDevice.ONLINE])
      meetAnycondition = meetAnycondition || active === true;
    if (filters[UserDevice.OFFLINE] || filters[UserDevice.ONLINE]) {
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        filters[UserDevice.ONLINE] &&
        filters[UserDevice.OFFLINE]
          ? true
          : filters[UserDevice.ONLINE]
          ? active === true
          : active === false;
    }
  } else if (filters[UserDevice.NO_DEVICE]) {
    meetAnycondition = meetAnycondition || device === undefined;
    meetAndCondition =
      (meetAndCondition === undefined ? true : meetAndCondition) &&
      device === undefined;
  }
  const mobileDeviceUser = userConfig;
  if (mobileDeviceUser?.hasDevice) {
    const active = mobileDeviceUser?.online;
    if (filters[UserMobileDevice.OFFLINE])
      meetAnycondition = meetAnycondition || active === false;
    if (filters[UserMobileDevice.ONLINE])
      meetAnycondition = meetAnycondition || active === true;
    if (filters[UserMobileDevice.OFFLINE] || filters[UserMobileDevice.ONLINE]) {
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        filters[UserMobileDevice.ONLINE] &&
        filters[UserMobileDevice.OFFLINE]
          ? true
          : filters[UserMobileDevice.ONLINE]
          ? active === true
          : active === false;
    }
  } else if (filters[UserMobileDevice.NO_DEVICE]) {
    meetAnycondition = meetAnycondition || device === undefined;
    meetAndCondition =
      (meetAndCondition === undefined ? true : meetAndCondition) &&
      device === undefined;
  }
  return logic === "OR" ? meetAnycondition : meetAndCondition;
};

const sortByEmail = (a: UsersProfile, b: UsersProfile): number => {
  if (a?.email?.toLowerCase() < b?.email?.toLowerCase()) {
    return -1;
  }
  if (a?.email?.toLowerCase() > b?.email?.toLowerCase()) {
    return 1;
  }
  return 0;
};
export const sortUsersByName = (a: UsersProfile, b: UsersProfile): number => {
  if (a.firstName !== null && b.firstName === null) return -1;
  if (a.firstName === null && b.firstName !== null) return 1;
  if (!a.firstName && !b.firstName) return sortByEmail(a, b);
  if (a?.firstName?.toLowerCase() < b?.firstName?.toLowerCase()) {
    return -1;
  }
  if (a?.firstName?.toLowerCase() > b?.firstName?.toLowerCase()) {
    return 1;
  }
};
export const reportUsersSearch = createSelector(
  organizationNodes,
  usersReady,
  (state: IAppState): DeviceData => state.devices.devices,
  (_: IAppState, users: UsersProfile[]) => users,
  (_: IAppState, __: any, searchValue: string): string => searchValue,
  (_: IAppState, __: any, ___: string, needSort: boolean): boolean => needSort,
  (organizationNodes, usersReady, devices, users, searchValue, needSort) => {
    if (!usersReady) return [];
    const res = Object.values(users).filter((el: UsersProfile) => {
      return el.roleName &&
        el.roleName !== RoleType.DISPLAY &&
        el.roleName !== RoleType.API_USER &&
        el.roleName !== RoleType.VIEWER &&
        el.status !== StatusType.PENDING
        ? true
        : false;
    });

    return needSort ? res.sort(sortUsersByName) : res;
  }
);

export const usersDevicesAfterGateCreator = () => {
  return createSelector(
    (state: IAppState): DeviceData => state.devices.devices,
    (_: IAppState, userId: string): string => userId,
    (devices, userId) => {
      return Object.values(devices)
        .filter((el) => el.type === DeviceType.userTag)
        .find((el) => el.userId === userId);
    }
  );
};

export const someUserHasDevice = () => {
  return createSelector(
    (state: IAppState): DeviceData => state.devices.devices,
    (_: IAppState, userIds: string[]): string[] => userIds,
    (devices, userIds) => {
      return Object.values(devices)
        .filter((el) => el.type === DeviceType.userTag)
        .some((el) => userIds.indexOf(el.userId) !== -1);
    }
  );
};

export const usersByIdCreator = () => {
  return createSelector(
    (state: IAppState): UsersData => state.users.users,
    (_: IAppState, ids: string[]): string[] => ids,
    (users, ids) => {
      // console.log(id, Object.keys(users).length, users[id]);
      if (Object.keys(users).length === 0) return [];

      const res = ids.map((el) => {
        return users[el];
      });
      return res;
    }
  );
};

export const userConfigSelectedCreator = () => {
  return createSelector(
    (state: IAppState): MobileDeviceUserData => state.users.mobileDeviceUser,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (_: IAppState, userId: string): string => userId,
    (mobileDeviceUser, activityStatuses, userId) => {
      return configForMobileDEvice(mobileDeviceUser, userId, activityStatuses);
    }
  );
};

export const userConfigSelected = createSelector(
  (state: IAppState): MobileDeviceUserData => state.users.mobileDeviceUser,
  (state: IAppState): DevicesStatusMap =>
    state.devicesStatuses.activityStatuses,
  (_: IAppState, userId: string): string => userId,
  (mobileDeviceUser, activityStatuses, userId) => {
    return configForMobileDEvice(mobileDeviceUser, userId, activityStatuses);
  }
);

export const configForMobileDEvice = (
  mobileDeviceUser: MobileDeviceUserData,
  userId: string,
  activityStatuses: DevicesStatusMap
) => {
  const data = mobileDeviceUser[userId];
  const enabled = data?.userConfigurations.some(
    (ek) => ek?.configValue.Enable === true
  );
  const configName = data?.userConfigurations[0].configName;
  const hasDevice =
    data?.devices?.length > 0 &&
    data.devices.some((el) => el?.deviceType === TagTypes.Android);

  const deviceId = hasDevice
    ? data.devices.find((el) => el?.deviceType === TagTypes.Android)?.deviceId
    : "";

  const online =
    data?.devices?.some((el) => el.status) ||
    data?.devices
      .filter((el) => el?.deviceType === TagTypes.Android)
      .some(
        (dev) => (activityStatuses[dev?.deviceId] as ActivityStatus)?.isActive
      );

  const continuesDetectionTime = hasDevice
    ? data.devices.find((el) => el?.deviceType === TagTypes.Android)
        ?.continuesDetectionTime
    : CONTINUOUS_DETECTION_TIME_DEFAULT;
  return {
    enabled,
    hasDevice,
    online,
    configName,
    deviceId,
    continuesDetectionTime,
  };
};
